import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  type ChannelSummary,
  useUpdateChannel,
} from "@/lib/channels";
import { DetailDrawer } from "@/components/ui/drawer";
import { type TabItem } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { formatRelative, formatNumber } from "@/lib/format";
import { pushToast } from "@/components/Toaster";

/**
 * ChannelDetailDrawer — channel detail as a tabbed DetailDrawer over the list
 * (local state, no new route). Replaces the old "Add single" modal whose
 * title/notes fields live here now. Tabs: Notes (editable title + notes) and
 * History (parsing meta + linked discussion group).
 */
export function ChannelDetailDrawer({
  channel,
  open,
  onOpenChange,
}: {
  channel: ChannelSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("notes");

  if (!channel) return null;

  const display = channel.username
    ? `@${channel.username}`
    : channel.tg_channel_id != null
      ? String(channel.tg_channel_id)
      : t("channels.unknown_channel");

  const tabs: TabItem[] = [
    {
      value: "notes",
      label: t("channels.drawer.tab_notes"),
      content: <NotesTab channel={channel} />,
    },
    {
      value: "history",
      label: t("channels.drawer.tab_history"),
      content: <HistoryTab channel={channel} />,
    },
  ];

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setActiveTab("notes");
      }}
      title={display}
      breadcrumbs={[
        { label: t("channels.drawer.breadcrumb") },
        { label: display },
      ]}
      status={{
        domain: "channel",
        value: channel.parsing_status,
        label: t(`channels.status.${channel.parsing_status}`),
      }}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      size="lg"
    />
  );
}

function NotesTab({ channel }: { channel: ChannelSummary }) {
  const { t } = useTranslation();
  const update = useUpdateChannel();
  const [title, setTitle] = useState(channel.title ?? "");
  const [notes, setNotes] = useState(channel.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the drawer switches to a different channel.
  useEffect(() => {
    setTitle(channel.title ?? "");
    setNotes(channel.notes ?? "");
    setError(null);
  }, [channel.id, channel.title, channel.notes]);

  const dirty =
    title !== (channel.title ?? "") || notes !== (channel.notes ?? "");

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        id: channel.id,
        // Sent as typed: an emptied field must clear the stored value.
        title,
        notes,
      });
      pushToast({ kind: "success", text: t("channels.drawer.saved") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("channels.drawer.save_error"));
    }
  };

  const requeue = () => {
    update.mutate({ id: channel.id, parsing_status: "queued" });
  };

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="space-y-1.5">
          <Label htmlFor={`ch-title-${channel.id}`}>{t("channels.field.title")}</Label>
          <Input
            id={`ch-title-${channel.id}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`ch-notes-${channel.id}`}>{t("channels.field.notes")}</Label>
          <textarea
            id={`ch-notes-${channel.id}`}
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          />
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex items-center justify-between gap-2">
          {channel.parsing_status !== "queued" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={requeue}
              loading={update.isPending}
              iconLeft={<RefreshCw size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {t("channels.requeue")}
            </Button>
          ) : (
            <span />
          )}
          <Button size="sm" disabled={!dirty} loading={update.isPending} onClick={save}>
            {t("channels.drawer.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function HistoryTab({ channel }: { channel: ChannelSummary }) {
  const { t } = useTranslation();

  const rel = (v: string | null) =>
    v ? formatRelative(v) : t("channels.drawer.never");

  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: t("channels.col.source"),
      value: t(`channels.source.${channel.source}`),
    },
    {
      label: t("channels.drawer.entity_type"),
      value: t(`channels.drawer.entity.${channel.entity_type}`),
    },
    {
      label: t("channels.drawer.subscribers"),
      value:
        channel.subscribers != null ? formatNumber(channel.subscribers) : "—",
    },
    {
      label: t("channels.drawer.language"),
      value: channel.language ?? "—",
    },
    {
      label: t("channels.drawer.last_scraped"),
      value: rel(channel.last_scraped_at),
    },
    {
      label: t("channels.drawer.last_post"),
      value: rel(channel.last_post_at),
    },
    {
      label: t("channels.drawer.created"),
      value: rel(channel.created_at),
    },
    {
      label: t("channels.drawer.updated"),
      value: rel(channel.updated_at),
    },
    {
      label: t("channels.drawer.external_id"),
      value: channel.external_id ? (
        <span className="font-mono text-xs">{channel.external_id}</span>
      ) : (
        "—"
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-3 text-sm font-semibold text-fg-primary">
            {t("channels.drawer.section_meta")}
          </h3>
          <dl className="divide-y divide-border">
            {rows.map((r) => (
              <div
                key={r.label}
                className="flex items-center justify-between gap-3 py-2 text-sm"
              >
                <dt className="text-fg-muted">{r.label}</dt>
                <dd className="text-right text-fg-primary">{r.value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="mb-2 text-sm font-semibold text-fg-primary">
            {t("channels.drawer.section_discussion")}
          </h3>
          {channel.linked_discussion_id != null ? (
            <p className="font-mono text-sm text-fg-secondary">
              {t("channels.drawer.discussion_id", {
                id: channel.linked_discussion_id,
              })}
            </p>
          ) : (
            <p className="text-sm text-fg-muted">
              {t("channels.drawer.discussion_none")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
